import { useState, useCallback, useRef, useEffect, useMemo, forwardRef, useImperativeHandle, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import Tooltip from '@/components/Tooltip';
import { useI18nText } from '@/lib/utils/i18nText';

/**
 * Sidebar toggle icon: three horizontal lines + a chevron arrow. The two states (pointing
 * left to collapse, pointing right to expand) are exact 180°-rotations of one another around
 * the chevron's center — one polyline rotated by CSS class instead of two swapped elements, so
 * the direction change animates instead of jump-cutting.
 */
function SidebarToggleIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none" aria-hidden>
      {/* Three horizontal lines */}
      <line x1="3" y1="5"  x2="11" y2="5"  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="3" y1="9"  x2="11" y2="9"  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="3" y1="13" x2="11" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <polyline
        points="13,6 16,9 13,12"
        className={`sidebar-toggle-chevron${collapsed ? '' : ' sidebar-toggle-chevron--collapse'}`}
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"
      />
    </svg>
  );
}

interface ResizableSidebarProps {
  children: ReactNode;
  /** Override the default storage key (auto-derived from the route). Pass
   *  `null` to disable width/collapsed persistence entirely. */
  storageKey?: string | null;
  /** Content rendered inside the collapsed strip (icon buttons, popovers…).
   *  When omitted the collapsed state shows only the expand toggle. */
  collapsedSlot?: ReactNode;
  /** Narrower floor + starting width for pages whose sidebar holds only short
   *  labels (e.g. the influencer profile's Payments/Statistics/Updates nav), where
   *  the shared 220px default is mostly empty space. Width is an inline style, so
   *  this can't be done from CSS on the parent layout. Defaults to the shared
   *  DEFAULT_WIDTH/MIN_WIDTH below; MAX_WIDTH is unchanged. */
  defaultWidth?: number;
  minWidth?: number;
}

/**
 * Imperative handle so a collapsed-rail icon can temporarily reveal the real (expanded)
 * sidebar `children` instead of duplicating that section's form in a small popover.
 * `showOverlay`/`hideOverlay` float the real content as a fixed layer on top of the main
 * content — the collapsed rail stays at its normal width and the main content never
 * reflows. The overlay closes on outside click, Escape, or `hideOverlay()`.
 */
export interface ResizableSidebarHandle {
  showOverlay: () => void;
  hideOverlay: () => void;
}

/* ── Shared defaults — cover every current usage (dashboard, campaigns,
 *    rosters, finances, campaign-context). Override via CSS on the parent
 *    layout if a page needs different ranges. ─────────────────────────── */
const DEFAULT_WIDTH = 220;
const MIN_WIDTH = 220;
const MAX_WIDTH = 480;

const MONGO_ID_RE = /\/[a-f0-9]{24}(?=\/|$)/g;
const UUID_RE = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi;

/**
 * Every sidebar's width/collapsed state is stored under this localStorage
 * prefix (either an explicit `storageKey` prop or a route-derived key — see
 * `deriveKeyFromPath` below). Exported so a global "reset local settings"
 * sweep can find every sidebar key without enumerating routes/pages.
 */
export const SIDEBAR_STORAGE_PREFIX = 'sidebar:';

/** Derive a stable storage key from a pathname by stripping ID segments so
 *  the user's chosen width persists across e.g. `/dashboard/campaign/{anyId}`. */
function deriveKeyFromPath(pathname: string): string {
  const stripped = pathname
    .replace(MONGO_ID_RE, '/:id')
    .replace(UUID_RE, '/:id');
  return `${SIDEBAR_STORAGE_PREFIX}${stripped || '/'}`;
}

function readNumber(key: string, fallback: number, min: number, max: number): number {
  if (typeof window === 'undefined') return fallback;
  const raw = window.localStorage.getItem(key);
  if (!raw) return fallback;
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function readBool(key: string): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(key) === '1';
}

/**
 * Resizable + collapsible left sidebar shell shared by Dashboard, Campaigns,
 * Rosters, Finances and the campaign-context sidebar.
 *
 * **Zero-config by design** — width range, default width, tooltip strings,
 * and localStorage persistence all use sensible shared defaults. Pages should
 * just render `<ResizableSidebar>{...content...}</ResizableSidebar>`.
 *
 * Width and collapsed state are auto-persisted to localStorage under a key
 * derived from the current route. Override via `storageKey`, or disable
 * persistence with `storageKey={null}`. Layout-specific CSS (height, sticky,
 * etc.) belongs on the parent layout class targeting the descendant
 * `.sidebar-resize-wrapper` / `.sidebar-collapsed`.
 */
const OVERLAY_LEFT = 44; // matches .sidebar-collapsed's width

const ResizableSidebar = forwardRef<ResizableSidebarHandle, ResizableSidebarProps>(function ResizableSidebar(
  { children, storageKey, collapsedSlot, defaultWidth = DEFAULT_WIDTH, minWidth = MIN_WIDTH },
  ref,
) {
  const { t } = useI18nText();
  const { pathname } = useLocation();
  const baseKey = useMemo(() => {
    if (storageKey === null) return null;
    if (storageKey) return storageKey;
    return deriveKeyFromPath(pathname);
  }, [storageKey, pathname]);

  const widthKey = baseKey ? `${baseKey}:width` : null;
  const collapsedKey = baseKey ? `${baseKey}:collapsed` : null;

  const [width, setWidth] = useState(() =>
    widthKey ? readNumber(widthKey, defaultWidth, minWidth, MAX_WIDTH) : defaultWidth,
  );
  const [collapsed, setCollapsed] = useState(() =>
    collapsedKey ? readBool(collapsedKey) : false,
  );
  // Collapsed-rail icons can reveal the real sidebar content as a floating overlay (see
  // ResizableSidebarHandle) instead of collapsing/expanding for real — `collapsed`/main-content
  // layout never change while this is open.
  const [overlayOpen, setOverlayOpen] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const resizingRef = useRef(false);
  const widthRef = useRef(width);
  widthRef.current = width;
  // The outer wrapper's width/min-width transition (collapse/expand slide) must NOT also
  // apply while live-dragging the resize handle, else every mousemove would animate toward
  // the new width instead of tracking the cursor instantly. Toggled directly on the DOM node
  // (not React state) so a drag doesn't cause a re-render per mousemove.
  const wrapperRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    showOverlay: () => setOverlayOpen(true),
    hideOverlay: () => setOverlayOpen(false),
  }), []);

  // Close the overlay on outside click (exempting body-portaled dropdowns/popovers that live
  // inside it — MultiSelect's `data-ms-portal` and Radix Popover's content wrapper) or Escape.
  useEffect(() => {
    if (!overlayOpen) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (target?.closest?.('[data-ms-portal], [data-radix-popper-content-wrapper]')) return;
      if (overlayRef.current && !overlayRef.current.contains(e.target as Node)) setOverlayOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOverlayOpen(false); };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [overlayOpen]);

  // Persist collapsed state immediately.
  useEffect(() => {
    if (!collapsedKey) return;
    if (collapsed) window.localStorage.setItem(collapsedKey, '1');
    else window.localStorage.removeItem(collapsedKey);
  }, [collapsed, collapsedKey]);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    resizingRef.current = true;
    if (wrapperRef.current) wrapperRef.current.style.transition = 'none';
    const startX = e.clientX;
    const startW = widthRef.current;
    const onMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      setWidth(Math.min(MAX_WIDTH, Math.max(minWidth, startW + (ev.clientX - startX))));
    };
    const onUp = () => {
      resizingRef.current = false;
      if (wrapperRef.current) wrapperRef.current.style.transition = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Persist on release (not every mousemove) to avoid thrashing localStorage.
      if (widthKey) window.localStorage.setItem(widthKey, String(widthRef.current));
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [widthKey, minWidth]);

  if (collapsed) {
    return (
      <div className="sidebar-collapsed" ref={wrapperRef}>
        <Tooltip content={t('Expand sidebar', '사이드바 펼치기')} placement="right">
          <button
            className="sidebar-top-toggle"
            onClick={() => setCollapsed(false)}
            aria-label={t('Expand sidebar', '사이드바 펼치기')}
          >
            <SidebarToggleIcon collapsed />
          </button>
        </Tooltip>
        {collapsedSlot}
        {overlayOpen && createPortal(
          <div ref={overlayRef} className="sidebar-overlay-panel" style={{ left: OVERLAY_LEFT, width }}>
            <div className="sidebar-content-column">
              {/* Same toggle button/spacing as the real expanded view so the form below sits
                  at the identical position in both states — closes the overlay, not `collapsed`. */}
              <Tooltip content={t('Close', '닫기')} placement="right">
                <button
                  className="sidebar-top-toggle"
                  onClick={() => setOverlayOpen(false)}
                  aria-label={t('Close', '닫기')}
                >
                  <SidebarToggleIcon collapsed={false} />
                </button>
              </Tooltip>
              {children}
            </div>
          </div>,
          document.body,
        )}
      </div>
    );
  }

  return (
    <div
      className="sidebar-resize-wrapper"
      style={{ width, minWidth: width }}
      ref={wrapperRef}
    >
      {/* Content column: toggle button at top, scrollable children below */}
      <div className="sidebar-content-column">
        <Tooltip content={t('Collapse sidebar', '사이드바 접기')} placement="right">
          <button
            className="sidebar-top-toggle"
            onClick={() => setCollapsed(true)}
            aria-label={t('Collapse sidebar', '사이드바 접기')}
          >
            <SidebarToggleIcon collapsed={false} />
          </button>
        </Tooltip>
        {children}
      </div>
      <div className="sidebar-resize-handle" onMouseDown={handleResizeStart}>
        <Tooltip content={t('Collapse sidebar', '사이드바 접기')}>
          <button
            type="button"
            className="sidebar-collapse-btn"
            aria-label={t('Collapse sidebar', '사이드바 접기')}
            onClick={(e) => { e.stopPropagation(); setCollapsed(true); }}
          >
            <i className="fa fa-chevron-left" />
          </button>
        </Tooltip>
      </div>
    </div>
  );
});

export default ResizableSidebar;
